import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import {
  ActionList,
  ActionListItem,
  Alert,
  ArrowLeftIcon,
  Box,
  Button,
  Card,
  CardBody,
  Dropdown,
  DropdownOverlay,
  Link,
  PasswordInput,
  SelectInput,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  Text,
  TextInput,
} from '@razorpay/blade/components';
import { statesOfIndia } from 'utils/StatesOfIndia';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { BASE_COMPENSATION_FLOOR_SETTINGS_QUERY_KEY, PTUpdateRequest } from 'api/companyOnboarding';
import api from 'api';
import { fetchLoggedInUser } from 'reducers/loggedInUser';
import { routePaths } from 'components/Routes/data';
import { AppError } from 'utils/AppError';
import Errors from 'components/ui/Form/Errors';
import { CredentialsType } from './types';
import { transformPTCredentials } from './utils';
import { trackSegment } from 'utils/segment';
import { ResetLocalOverridingStyles } from 'components/RunPayroll/ComplianceActions/styles';
import useFeature from 'hooks/useFeature';

const INITIAL_CREDENTIALS_COUNT = 5;
const CREDENTIALS_INCREMENT = 5;

const FormWrapper = styled.form(
  ({ theme }) => `
  width: 100%;
  padding-bottom: ${theme.spacing[10]}px;
`,
);

const NoDividerTable = styled.div(
  ({ theme }) => `
  overflow-x: auto;
  .cell-wrapper {
    border-bottom: none !important;
    padding-left: ${theme.spacing[2]}px !important;
    padding-right: ${theme.spacing[2]}px !important;
  }
  [data-blade-component='table-header-cell'] {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
`,
);

const PasswordFieldScope = styled.div(
  ({ theme }) => `
  button[aria-label='Show password'],
  button[aria-label='Hide password'] {
    width: ${theme.spacing[5]}px !important;
    min-width: ${theme.spacing[5]}px !important;
    max-width: ${theme.spacing[5]}px !important;
    flex: 0 0 ${theme.spacing[5]}px !important;
  }`,
);

const PtSetup = () => {
  const currentOrganization = useSelector(loggedInUserSelectors.currentOrganization);
  const dispatch = useDispatch();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { isReArchMilestone1Enabled: isRearch } = useFeature('reArchMilestone1');
  const { isBaseCompensationFloorEnabled } = useFeature('baseCompensationFloor');
  const [isPTEnabled, setIsPTEnabled] = useState(!!currentOrganization?.isPt);
  const [isPTPaymentEnabled, setIsPTPaymentEnabled] = useState(
    !!currentOrganization?.paymentOptions?.payPt,
  );
  const [applyBaseCompFloorToPt, setApplyBaseCompFloorToPt] = useState(false);
  const [applyBaseCompFloorToLwf, setApplyBaseCompFloorToLwf] = useState(false);

  const { data: baseCompensationFloorSettings } = useQuery(
    [BASE_COMPENSATION_FLOOR_SETTINGS_QUERY_KEY],
    api.companyOnboarding.getBaseCompensationFloorSettings,
    { enabled: isRearch && isBaseCompensationFloorEnabled },
  );

  useEffect(() => {
    if (baseCompensationFloorSettings) {
      setApplyBaseCompFloorToPt(baseCompensationFloorSettings.applyBaseCompFloorToPt);
      setApplyBaseCompFloorToLwf(baseCompensationFloorSettings.applyBaseCompFloorToLwf);
    }
  }, [baseCompensationFloorSettings]);

  const existingCredentialsCount = currentOrganization?.externalCredentials?.ptInfo
    ? Object.keys(currentOrganization.externalCredentials.ptInfo).length
    : 0;
  const maxCredentialsAllowed = Math.max(INITIAL_CREDENTIALS_COUNT, existingCredentialsCount);

  const [duplicateStateError, setDuplicateStateError] = useState<string | null>(null);

  // Controlled per-row state: Blade form components don't render native named
  // inputs, so we drive the credentials grid from React state and submit from
  // state instead of via FormData.
  const [credentialRows, setCredentialRows] = useState<CredentialsType[]>(() =>
    transformPTCredentials({
      credentials: currentOrganization?.externalCredentials,
      maximumCredentialsAllowed: maxCredentialsAllowed,
    }),
  );

  const updateRow = (idx: number, patch: Partial<CredentialsType>) => {
    setCredentialRows((prev) => prev.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  };

  const getAvailableStatesForRow = useCallback(
    (idx: number) => {
      const otherSelectedStates = new Set(
        credentialRows
          .filter((_, i) => i !== idx)
          .map((r) => r.state)
          .filter((s): s is number => s !== null),
      );
      return statesOfIndia.filter((state) => !otherSelectedStates.has(state.code));
    },
    [credentialRows],
  );

  const updatePTMutation = useMutation(
    ({
      isPTEnabled,
      isPTPaymentEnabled,
      credentials,
      applyBaseCompFloorToPt,
      applyBaseCompFloorToLwf,
    }: PTUpdateRequest) =>
      api.companyOnboarding.updatePTSetup({
        isPTEnabled,
        isPTPaymentEnabled,
        credentials,
        applyBaseCompFloorToPt,
        applyBaseCompFloorToLwf,
      }),
    {
      onSuccess: (data) => {
        trackSegment('onboarding.setup_compliance.professional_tax.save_and_next_click');
        queryClient.invalidateQueries([BASE_COMPENSATION_FLOOR_SETTINGS_QUERY_KEY]);
        dispatch(fetchLoggedInUser());
        history.push(routePaths.companyOnboarding.compliance.pf);
      },
      onError: (error: typeof AppError) => {},
    },
  );

  const validateUniqueStates = (credentials: CredentialsType[]): string | null => {
    const states = credentials.filter((cred) => cred.state !== null).map((cred) => cred.state);
    const uniqueStates = new Set(states);
    if (states.length !== uniqueStates.size) {
      return 'Each state can only have one set of PT credentials. Please remove duplicate states.';
    }
    return null;
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setDuplicateStateError(null);

    const credentials: CredentialsType[] = credentialRows
      .filter((row) => row.state !== null)
      .map((row) => ({
        state: row.state,
        username: row.username,
        password: row.password,
        info: row.info ?? '',
      }));

    const duplicateError = validateUniqueStates(credentials);
    if (duplicateError) {
      setDuplicateStateError(duplicateError);
      return;
    }

    const requestObject: PTUpdateRequest = {
      isPTEnabled,
      isPTPaymentEnabled: !isPTEnabled ? false : isPTPaymentEnabled,
      credentials,
      applyBaseCompFloorToPt,
      applyBaseCompFloorToLwf,
    };

    updatePTMutation.mutate(requestObject);
  };

  const handleAddMore = () => {
    setCredentialRows((prev) => [
      ...prev,
      ...Array.from({ length: CREDENTIALS_INCREMENT }, () => ({
        state: null,
        username: '',
        password: '',
        info: '',
      })),
    ]);
  };

  const isFormBeingSubmitted = updatePTMutation.status === 'loading';

  return (
    <ResetLocalOverridingStyles>
      <FormWrapper onSubmit={handleSubmit}>
        <Box
          display="flex"
          flexDirection="column"
          gap="spacing.8"
          paddingBottom="spacing.8"
          width="100%"
          maxWidth="512px"
          marginX="auto">
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            gap="spacing.4"
            width="100%">
            <Box display="flex" flexDirection="column" gap="spacing.2">
              <Text size="large" weight="semibold" textAlign="left">
                Professional Tax
              </Text>
              <Text size="medium" color="surface.text.gray.muted" textAlign="left">
                Deduct professional tax from salaries
              </Text>
            </Box>
            <Switch
              accessibilityLabel="Professional Tax"
              isChecked={isPTEnabled}
              isDisabled={isFormBeingSubmitted}
              onChange={({ isChecked }) => setIsPTEnabled(isChecked)}
            />
          </Box>

          {isPTEnabled && (
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
              gap="spacing.4"
              width="100%">
              <Box display="flex" flexDirection="column" gap="spacing.2">
                <Text size="large" weight="semibold" textAlign="left">
                  PT Payment Enabled
                </Text>
                <Text size="medium" color="surface.text.gray.muted" textAlign="left">
                  Pay professional tax on behalf of the company
                </Text>
              </Box>
              <Switch
                accessibilityLabel="PT Payment Enabled"
                isChecked={isPTPaymentEnabled}
                isDisabled={isFormBeingSubmitted}
                onChange={({ isChecked }) => setIsPTPaymentEnabled(isChecked)}
              />
            </Box>
          )}

          {isPTPaymentEnabled && (
            <Card
              elevation="none"
              borderRadius="large"
              backgroundColor="surface.background.gray.intense">
              <CardBody>
                <Box display="flex" flexDirection="column" gap="spacing.6" width="100%">
                  <Box display="flex" flexDirection="column" gap="spacing.2">
                    <Text size="large" weight="semibold" textAlign="left">
                      Link your state PT portals
                    </Text>
                    <Text size="medium" color="surface.text.gray.muted" textAlign="left">
                      We'll use these to pay your PT dues and file returns on your behalf. Add one
                      row for each state your company operates in
                    </Text>
                  </Box>
                  <NoDividerTable>
                    <Table
                      data={{ nodes: credentialRows.map((row, idx) => ({ ...row, id: idx })) }}
                      selectionType="none"
                      gridTemplateColumns="180px 180px 180px 180px">
                      {(tableData) => (
                        <>
                          <TableHeader>
                            <TableHeaderRow>
                              <TableHeaderCell>State</TableHeaderCell>
                              <TableHeaderCell>Username</TableHeaderCell>
                              <TableHeaderCell>Password</TableHeaderCell>
                              <TableHeaderCell>Additional Info (Optional)</TableHeaderCell>
                            </TableHeaderRow>
                          </TableHeader>
                          <TableBody>
                            {tableData.map((row) => {
                              const idx = row.id as number;
                              const availableStates = getAvailableStatesForRow(idx);
                              return (
                                <TableRow key={idx} item={row}>
                                  <TableCell>
                                    <Box width="100%">
                                      <Dropdown selectionType="single">
                                        <SelectInput
                                          label=""
                                          accessibilityLabel="State"
                                          placeholder="Choose State"
                                          value={row.state?.toString() ?? ''}
                                          onChange={({ values }) => {
                                            const updatedState = values[0];
                                            updateRow(idx, {
                                              state: updatedState
                                                ? parseInt(updatedState, 10)
                                                : null,
                                            });
                                          }}
                                          isDisabled={isFormBeingSubmitted}
                                        />
                                        <DropdownOverlay minWidth="260px">
                                          <ActionList>
                                            {availableStates.map((state) => (
                                              <ActionListItem
                                                key={state.code}
                                                title={state.name}
                                                value={state.code.toString()}
                                              />
                                            ))}
                                          </ActionList>
                                        </DropdownOverlay>
                                      </Dropdown>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <TextInput
                                      label=""
                                      accessibilityLabel={`Username row ${idx + 1}`}
                                      placeholder="Username"
                                      value={row.username}
                                      onChange={({ value }) =>
                                        updateRow(idx, { username: value ?? '' })
                                      }
                                      isDisabled={isFormBeingSubmitted}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <PasswordFieldScope>
                                      <PasswordInput
                                        label=""
                                        accessibilityLabel={`Password row ${idx + 1}`}
                                        placeholder="Password"
                                        value={row.password}
                                        onChange={({ value }) =>
                                          updateRow(idx, { password: value ?? '' })
                                        }
                                        isDisabled={isFormBeingSubmitted}
                                      />
                                    </PasswordFieldScope>
                                  </TableCell>
                                  <TableCell>
                                    <TextInput
                                      label=""
                                      accessibilityLabel={`Additional info row ${idx + 1}`}
                                      placeholder="Additional info"
                                      value={row.info ?? ''}
                                      onChange={({ value }) =>
                                        updateRow(idx, { info: value ?? '' })
                                      }
                                      isDisabled={isFormBeingSubmitted}
                                    />
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </>
                      )}
                    </Table>
                  </NoDividerTable>

                  <Button
                    type="button"
                    variant="tertiary"
                    onClick={handleAddMore}
                    isDisabled={isFormBeingSubmitted}
                    isFullWidth>
                    Add More
                  </Button>
                </Box>
              </CardBody>
            </Card>
          )}

          {isRearch && isBaseCompensationFloorEnabled && (
            <Card
              elevation="none"
              borderRadius="large"
              backgroundColor="surface.background.gray.intense">
              <CardBody>
                <Box display="flex" flexDirection="column" gap="spacing.6" width="100%">
                  <Box display="flex" flexDirection="column" gap="spacing.2">
                    <Text size="large" weight="semibold" textAlign="left">
                      CoSS 2020 (Labour Code)
                    </Text>
                    <Text size="medium" color="surface.text.gray.muted" textAlign="left">
                      Enforce that these wages are at least 50% of Base Compensation as required by
                      the Code on Social Security 2020.
                    </Text>
                  </Box>
                  <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap="spacing.4"
                    width="100%">
                    <Text size="medium" weight="regular" textAlign="left">
                      Apply Base Compensation floor to PT wages
                    </Text>
                    <Switch
                      accessibilityLabel="Apply Base Compensation floor to PT wages"
                      isChecked={applyBaseCompFloorToPt}
                      isDisabled={isFormBeingSubmitted}
                      onChange={({ isChecked }) => setApplyBaseCompFloorToPt(isChecked)}
                    />
                  </Box>
                  <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap="spacing.4"
                    width="100%">
                    <Text size="medium" weight="regular" textAlign="left">
                      Apply Base Compensation floor to LWF wages
                    </Text>
                    <Switch
                      accessibilityLabel="Apply Base Compensation floor to LWF wages"
                      isChecked={applyBaseCompFloorToLwf}
                      isDisabled={isFormBeingSubmitted}
                      onChange={({ isChecked }) => setApplyBaseCompFloorToLwf(isChecked)}
                    />
                  </Box>
                </Box>
              </CardBody>
            </Card>
          )}

          {updatePTMutation.status === 'error' && (
            <Alert
              color="negative"
              description={<Errors errorResponse={updatePTMutation.error} />}
              isDismissible={false}
              isFullWidth
            />
          )}
          {duplicateStateError && (
            <Alert
              color="negative"
              description={duplicateStateError}
              isDismissible={false}
              isFullWidth
            />
          )}

          <Box
            width="100%"
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            paddingTop="spacing.5">
            <Link
              variant="button"
              color="neutral"
              icon={ArrowLeftIcon}
              iconPosition="left"
              onClick={() => {
                window.location.href = routePaths.phpRoutes.dashboard;
              }}>
              Back to setup guide
            </Link>
            <Box display="flex" gap="spacing.4">
              <Button
                variant="secondary"
                onClick={() => history.push(routePaths.companyOnboarding.compliance.tds)}>
                Back
              </Button>
              <Button variant="primary" type="submit" isLoading={isFormBeingSubmitted}>
                Save & Next
              </Button>
            </Box>
          </Box>
        </Box>
      </FormWrapper>
    </ResetLocalOverridingStyles>
  );
};

export default PtSetup;
